// Components
import { Paper, Text, Group, Stack, Collapse, Box } from "@mantine/core";
import {
  IconPhoneOutgoing,
  IconPhoneIncoming,
  IconPhoneOff,
  IconChevronDown,
} from "@tabler/icons-react";

// Hooks
import { useState, useMemo } from "react";

// Utils
import { formatFHIRDateTime } from "@benchmark/ui/utils";
import {
  getCallMetadata,
  formatCallDuration,
  getCallTranscript,
} from "@/utils/fhir/communication";

// Types
import type { CallItemProps } from "@/types/domain/messaging";

// Styles
import classes from "./CallItem.module.css";

/** Call duration threshold (ms) below which a call is considered "likely missed". */
const LIKELY_MISSED_THRESHOLD_MS = 60000;

/**
 * Displays a phone call in a card format with expandable transcript.
 * @agentic connections
 */
export const CallItem = ({ communication, isOutbound }: CallItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract call metadata
  const metadata = useMemo(
    () => getCallMetadata(communication),
    [communication],
  );
  const transcript = useMemo(
    () => getCallTranscript(communication),
    [communication],
  );

  // Determine call direction (use extension if available, fallback to isOutbound prop)
  const direction = metadata.direction ?? (isOutbound ? "outbound" : "inbound");
  const isMissed = metadata.state === "missed";

  // Determine if call is "likely missed" (under 60 seconds and not already marked as missed)
  const isLikelyMissed =
    !isMissed &&
    metadata.durationMs !== undefined &&
    metadata.durationMs < LIKELY_MISSED_THRESHOLD_MS;

  // Extract timestamp (use sent for call start time)
  const timestamp = communication.sent || communication.meta?.lastUpdated;

  // Format timestamp
  const formattedTime = useMemo(() => {
    if (!timestamp) return "";
    try {
      return formatFHIRDateTime(timestamp);
    } catch {
      return "";
    }
  }, [timestamp]);

  // Format duration
  const formattedDuration = useMemo(() => {
    if (metadata.durationMs === undefined) return undefined;
    return formatCallDuration(metadata.durationMs);
  }, [metadata.durationMs]);

  // Get direction icon - use missed icon for both missed and likely missed
  const DirectionIcon =
    isMissed || isLikelyMissed
      ? IconPhoneOff
      : direction === "outbound"
        ? IconPhoneOutgoing
        : IconPhoneIncoming;

  // Get call type label
  const callTypeLabel = isMissed
    ? "Missed Call"
    : isLikelyMissed
      ? "Likely Missed Call"
      : direction === "outbound"
        ? "Outbound Call"
        : "Inbound Call";

  // Determine card variant class - use missed styling for likely missed too
  const variantClass =
    isMissed || isLikelyMissed
      ? classes.missedCall
      : direction === "outbound"
        ? classes.outboundCall
        : classes.inboundCall;

  // Only show expand if there's a transcript
  const hasTranscript = transcript && transcript.trim().length > 0;

  return (
    <Paper className={`${classes.callCard} ${variantClass}`} mb="sm">
      {/* Header - always visible, clickable to expand */}
      <Box
        className={classes.callHeader}
        onClick={() => hasTranscript && setIsExpanded(!isExpanded)}
        role={hasTranscript ? "button" : undefined}
        tabIndex={hasTranscript ? 0 : undefined}
        onKeyDown={(e) => {
          if (hasTranscript && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
        aria-expanded={hasTranscript ? isExpanded : undefined}
        aria-label={
          hasTranscript
            ? `${callTypeLabel}, click to view transcript`
            : callTypeLabel
        }
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            {/* Call icon */}
            <Box className={classes.callIcon}>
              <DirectionIcon size={18} />
            </Box>

            {/* Call info */}
            <Stack gap={2}>
              <Group gap="xs">
                <Text size="sm" fw={500} className={classes.primaryText}>
                  {callTypeLabel}
                </Text>
                {formattedDuration && (
                  <>
                    <Text size="sm" className={classes.dimmedText}>
                      •
                    </Text>
                    <Text size="sm" className={classes.dimmedText}>
                      {formattedDuration}
                    </Text>
                  </>
                )}
              </Group>
              {formattedTime && (
                <Text size="xs" className={classes.dimmedText}>
                  {formattedTime}
                </Text>
              )}
            </Stack>
          </Group>

          {/* Expand chevron (only if has transcript) */}
          {hasTranscript && (
            <IconChevronDown
              size={18}
              className={`${classes.chevron} ${isExpanded ? classes.chevronExpanded : ""}`}
            />
          )}
        </Group>
      </Box>

      {/* Transcript area - expandable */}
      {hasTranscript && (
        <Collapse in={isExpanded}>
          <Box className={classes.transcriptArea}>
            <Text size="xs" fw={500} className={classes.dimmedText} mb="xs">
              Transcript
            </Text>
            <Text
              size="sm"
              className={`${classes.transcriptText} ${classes.primaryText}`}
            >
              {transcript}
            </Text>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
};
